import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { format } from 'date-fns';
import type { MetricsSnapshot } from '../lib/api';
import { formatRate, intlLocale } from '../i18n/formats';
import { useLocale } from '../i18n/LocaleProvider';
import {
  AXIS_TICK,
  GRID_STROKE,
  LEGEND_STYLE,
  SERIES_FALLBACK,
  THROUGHPUT_COLORS,
  TOOLTIP_ITEM_STYLE,
  TOOLTIP_LABEL_STYLE,
  TOOLTIP_STYLE,
} from './chartTheme';

interface Props {
  history: MetricsSnapshot[];
}

export function ThroughputChart({ history }: Props) {
  const { t, locale } = useLocale();

  if (history.length === 0) {
    return (
      <div className="h-48 flex items-center justify-center text-gray-500 text-sm">
        {t('charts.noHistory')}
      </div>
    );
  }

  const queueNames = Object.keys(history[0]?.queues ?? {});

  const data = history.map((snap) => ({
    time: format(new Date(snap.timestamp), 'HH:mm'),
    ...Object.fromEntries(
      queueNames.map((q) => [q, snap.queues[q]?.processingRate ?? 0]),
    ),
  }));

  return (
    <ResponsiveContainer width="100%" height={220}>
      <AreaChart data={data} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
        <defs>
          {queueNames.map((q) => (
            <linearGradient
              key={q}
              id={`grad-${q}`}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop
                offset="5%"
                stopColor={THROUGHPUT_COLORS[q] ?? SERIES_FALLBACK}
                stopOpacity={0.28}
              />
              <stop
                offset="95%"
                stopColor={THROUGHPUT_COLORS[q] ?? SERIES_FALLBACK}
                stopOpacity={0}
              />
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
        <XAxis
          dataKey="time"
          tick={AXIS_TICK}
          axisLine={false}
          tickLine={false}
          interval="preserveStartEnd"
        />
        <YAxis
          tick={AXIS_TICK}
          axisLine={false}
          tickLine={false}
          unit={t('charts.perMinuteUnit')}
          // recharts prints a raw JS number, so an axis and a tooltip left alone read `12.3`
          // on a page that is otherwise entirely in German. `toLocaleString` rather than
          // `formatRate` here: an axis of whole numbers should stay whole.
          tickFormatter={(value) =>
            Number(value).toLocaleString(intlLocale(locale))
          }
        />
        <Tooltip
          contentStyle={TOOLTIP_STYLE}
          labelStyle={TOOLTIP_LABEL_STYLE}
          itemStyle={TOOLTIP_ITEM_STYLE}
          formatter={(value) =>
            `${formatRate(Number(value), locale)}${t('charts.perMinuteUnit')}`
          }
        />
        <Legend wrapperStyle={LEGEND_STYLE} iconType="circle" iconSize={8} />
        {queueNames.map((q) => (
          <Area
            key={q}
            type="monotone"
            dataKey={q}
            stroke={THROUGHPUT_COLORS[q] ?? SERIES_FALLBACK}
            fill={`url(#grad-${q})`}
            strokeWidth={2}
            dot={false}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
}
